import { useEffect, useState } from 'react';
import clsx from 'clsx';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import { makeStyles } from '@material-ui/core';

import { Logo, ProgressFlow } from '@care/react-component-lib';
import { theme } from '@care/material-ui-theme';

import {
  CLIENT_FEATURE_FLAGS,
  CZEN_BASE_PATH,
  FLAGS_747,
  FLOWS,
  VERTICALS_NAMES,
} from '@/constants';
import { YesOrNoAnswer } from '@/__generated__/globalTypes';
import { getFeatureFlagValues, isTestCellExactId } from '@/utilities/featureFlagHelper';
import getVertical from '@/utilities/verticalUtils';
import { urlHelper } from '@/utilities/urlHelper';
import useIsSinglePageEnrollment from '@/components/hooks/useIsSinglePageEnrollment';
import { useCondensedAccountCreation } from '@/components/hooks/useCondensedAccountCreation';
import useFlowHelper from '@/components/hooks/useFlowHelper';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';
import { TestCellMap } from '@/types/providerCC';
import { FlowType } from '@/types/common';
import { useProviderCCState, useSeekerCCState, useSeekerState } from './AppState';
import useMVEnrollmentSteps from './hooks/useMVEnrollmentSteps';

const {
  publicRuntimeConfig: { CZEN_GENERAL },
} = getConfig();

type FeatureFlagValues = [TestCellMap, boolean, boolean, string];

const useStyles = makeStyles(() => ({
  wrapper: {
    '& .progress-flow-status': {
      borderBottom: 0,
    },
  },
  bottomBorder: {
    borderBottom: `1px solid ${theme.palette.care?.grey[300]}`,
  },
}));

const {
  CZEN_TESTCELL_REFERENCEID_MAP,
  PROVIDER_CC_FREE_GATED_EXPERIENCE,
  PROJECT_747_FLAG,
  TTP_ASSET_URL,
} = CLIENT_FEATURE_FLAGS;

const {
  CC_ENROLLMENT_REMOVE_PHOTO_TEST_CELL,
  CC_ENROLLMENT_REMOVE_BIO_TEST_CELL,
  CC_ENROLLMENT_REMOVE_ABOUTME_TEST_CELL,
  MOBILE_CC_ENROLLMENT_REMOVE_PHOTO_TEST_CELL,
  MOBILE_CC_ENROLLMENT_REMOVE_BIO_TEST_CELL,
  MOBILE_CC_ENROLLMENT_REMOVE_ABOUTME_TEST_CELL,
  CC_DESKTOP_PROVIDER_ENROLLMENT_MFE_NON_FG,
  MOBILE_PROVIDER_ENROLLMENT_CC_MFE_NON_FG,
  CC_DESKTOP_PROVIDER_ENROLLMENT_MFE_FG_WEB_PAYMENTS,
  MOBILE_PROVIDER_ENROLLMENT_CC_MFE_FG_WEB_PAYMENTS,
  M4_747_DT,
  M4_747_MW,
} = FLAGS_747;

const removePhotoFlags = [
  `${CC_ENROLLMENT_REMOVE_PHOTO_TEST_CELL}`,
  `${MOBILE_CC_ENROLLMENT_REMOVE_PHOTO_TEST_CELL}`,
];
const removeBioFlags = [
  `${CC_ENROLLMENT_REMOVE_BIO_TEST_CELL}`,
  `${MOBILE_CC_ENROLLMENT_REMOVE_BIO_TEST_CELL}`,
];
const removeAboutMeFlags = [
  `${CC_ENROLLMENT_REMOVE_ABOUTME_TEST_CELL}`,
  `${MOBILE_CC_ENROLLMENT_REMOVE_ABOUTME_TEST_CELL}`,
];
const expectedWebPaymentCells = [
  `${CC_DESKTOP_PROVIDER_ENROLLMENT_MFE_FG_WEB_PAYMENTS}`,
  `${MOBILE_PROVIDER_ENROLLMENT_CC_MFE_FG_WEB_PAYMENTS}`,
  `${M4_747_DT}`,
  `${M4_747_MW}`,
];
const project747Flags = [
  ...removePhotoFlags,
  ...removeBioFlags,
  ...removeAboutMeFlags,
  ...expectedWebPaymentCells,
];

const ProgressFlowNavbar = () => {
  const { pathname } = useRouter();
  const vertical = getVertical(pathname);
  const classes = useStyles();
  const {
    stepNumber,
    totalSteps,
    currentFlow,
    hideStepNumber,
    hideProgressIndicator,
    showBottomBorder,
  } = useFlowHelper();
  const { isSeekerSinglePageEnrollment } = useIsSinglePageEnrollment();
  const { condensedAccountCreateFlag } = useCondensedAccountCreation();
  const { isHomeAddress } = useSeekerCCState().instantBook;
  const { isUserAssignedToSkipSteps } = useSeekerState();
  const { testCellCookieProvider, isWebBasedPaymentReturningUser } = useProviderCCState();
  const { mvHideSteps, mvLabel, mvTotalSteps } = useMVEnrollmentSteps({ stepNumber });
  const [steps, setSteps] = useState(6);

  const [testCellFlagMap, providerCCFreeGatedExperience, project747Flag, logoUrl] =
    getFeatureFlagValues<FeatureFlagValues>([
      CZEN_TESTCELL_REFERENCEID_MAP,
      PROVIDER_CC_FREE_GATED_EXPERIENCE,
      PROJECT_747_FLAG,
      TTP_ASSET_URL,
    ]);
  const altLogoUrl = urlHelper(logoUrl) || `${CZEN_GENERAL}/s/d/ttp/care-logo.svg`;

  const nonFreegatedCells = [
    `${CC_DESKTOP_PROVIDER_ENROLLMENT_MFE_NON_FG}`,
    `${MOBILE_PROVIDER_ENROLLMENT_CC_MFE_NON_FG}`,
    ``,
  ];
  const freegatedCells = Object.fromEntries(
    Object.entries(testCellFlagMap || {}).filter(([, value]) => !nonFreegatedCells.includes(value))
  );

  const providerCCEnrollmentTestCells = Object.keys(freegatedCells ?? {});
  const isTestCellFeatureFlagOn =
    providerCCEnrollmentTestCells && providerCCEnrollmentTestCells.length > 0;
  const isProviderCCAccountCreationFlow = currentFlow === FlowType.PROVIDER_CC_ACCOUNT_CREATION;
  const isFreeGatedProviderCCAccountCreationFlow =
    providerCCFreeGatedExperience && isProviderCCAccountCreationFlow;

  const condensedAccountCreateFlagValue = condensedAccountCreateFlag?.value === 2;
  const hideForCondensedAccountCreation =
    condensedAccountCreateFlagValue && vertical === VERTICALS_NAMES.CHILD_CARE;

  let label = '';

  let hideSteps =
    hideForCondensedAccountCreation ||
    isSeekerSinglePageEnrollment ||
    hideStepNumber ||
    !currentFlow ||
    isUserAssignedToSkipSteps === YesOrNoAnswer.YES;

  // on childcare freeGated flow, we should hide 1 step of account creation
  let currentStep =
    isFreeGatedProviderCCAccountCreationFlow && stepNumber !== 1 ? stepNumber - 1 : stepNumber;
  let totalStepsNumber = isFreeGatedProviderCCAccountCreationFlow ? totalSteps - 1 : totalSteps;
  if (
    [
      FLOWS.SEEKER_INSTANT_BOOK.name,
      FLOWS.SEEKER_INSTANT_BOOK_SHORT.name,
      FLOWS.SEEKER_INSTANT_BOOK_SHORT_MATCH.name,
    ].includes(currentFlow as string)
  ) {
    totalStepsNumber = isHomeAddress ? totalSteps + 3 : totalSteps + 4;
  }

  if (currentFlow === FlowType.PROVIDER_CC_INFORMATIONAL_STEPS) {
    hideSteps = true;
    currentStep = 0;

    if (stepNumber === 1) {
      label = 'Great start!';
    } else if (stepNumber === 2) {
      label = 'Nice!';
    }
  }
  if (currentFlow === FlowType.PROVIDER_CC_PROFILE_DETAILS) {
    label = 'Build your profile';
    totalStepsNumber = steps;
  }

  if (currentFlow === FlowType.PROVIDER_MV_DETAILS) {
    label = mvLabel;
    hideSteps = mvHideSteps;
    totalStepsNumber = mvTotalSteps;
  }

  if (isProviderCCAccountCreationFlow) {
    if (isWebBasedPaymentReturningUser) {
      label = 'Build your profile';
      hideSteps = true;
    } else {
      label = 'Create your account';
      totalStepsNumber = 2;
    }
  }
  if (currentFlow === FlowType.PROVIDER_MV_ACCOUNT_CREATION) {
    label = 'Create your account';
    totalStepsNumber = 3;
  }

  function evaluateStepsNumber(activeCells: Array<string>) {
    if (removePhotoFlags.some((cell) => activeCells.includes(cell))) {
      setSteps(5);
    }
    if (removeBioFlags.some((cell) => activeCells.includes(cell))) {
      setSteps(4);
    }
    if (removeAboutMeFlags.some((cell) => activeCells.includes(cell))) {
      setSteps(3);
    }
    if (expectedWebPaymentCells.some((cell) => activeCells.includes(cell))) {
      setSteps(3);
    }
  }

  const logEvent = () => {
    AnalyticsHelper.logEvent({
      name: 'Consumer Logo Clicked',
      data: {
        screen_name: 'Single Page Enrollment',
      },
    });
  };

  useEffect(() => {
    if (project747Flag && isTestCellFeatureFlagOn) {
      const testCellValueCollection: Array<string> = providerCCEnrollmentTestCells
        .filter((testId) => isTestCellExactId(testId, testCellCookieProvider))
        .map((id) => testCellFlagMap[id]);

      const activeCells = testCellValueCollection.filter((item) => project747Flags.includes(item));
      evaluateStepsNumber(activeCells);
    }
  }, []);

  return (
    <div className={clsx(classes.wrapper, showBottomBorder && classes.bottomBorder)}>
      <ProgressFlow
        step={currentStep}
        total={totalStepsNumber}
        label={label}
        hideSteps={hideSteps}
        hideProgress={hideProgressIndicator}
        logo={
          <div
            onKeyDown={(e) => {
              if (isSeekerSinglePageEnrollment && (e.key === 'Enter' || e.key === 'NumpadEnter')) {
                logEvent();
              }
            }}
            tabIndex={0}
            role="button"
            data-testid="care-logo"
            onClick={() => {
              if (isSeekerSinglePageEnrollment) {
                logEvent();
              }
            }}>
            <Logo altLogoUrl={altLogoUrl} variant="careAlt" />
          </div>
        }
        link={isSeekerSinglePageEnrollment ? CZEN_BASE_PATH : ''}
      />
    </div>
  );
};

ProgressFlowNavbar.defaultProps = {
  currentFlow: '',
};

export default ProgressFlowNavbar;
